// src/components/coupon/CouponTable.tsx
import * as React from "react";
import { Switch, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { CouponListItem } from "src/api/coupon/couponApi";
import { ManageBtns } from "src/components/common/TableButtons";
import CustomButton from "src/components/common/CustomButton";

type Props = {
  list: CouponListItem[];
  loading: boolean;
  disabled?: boolean;
  onEdit: (item: CouponListItem) => void;
  onDelete: (item: CouponListItem) => void;
  onCopy: (item: CouponListItem) => void;
  onToggleActive: (item: CouponListItem) => void;
};

export default function CouponTable({
  list,
  loading,
  disabled = false,
  onEdit,
  onDelete,
  onCopy,
  onToggleActive,
}: Props) {
  const getCouponTypeLabel = (type: string) => {
    switch (type) {
      case "PUBLIC":
        return "공개";
      case "PRIVATE":
        return "개인";
      case "AUTO":
        return "자동발급";
      case "EVENT":
        return "이벤트";
      case "FREE_SHIPPING":
        return "무료 배송";
      default:
        return type;
    }
  };

  const getScopeLabel = (scope: string) => {
    switch (scope) {
      case "CART":
        return "장바구니";
      case "PRODUCT":
        return "상품";
      case "CATEGORY":
        return "카테고리";
      default:
        return scope;
    }
  };

  const columns: ColumnsType<CouponListItem> = [
    { title: "ID", dataIndex: "couponId", align: "center", width: 60 },
    {
      title: "쿠폰명",
      dataIndex: "couponName",
      align: "center",
      width: 150,
      render: (value) => (
        <Typography.Text strong ellipsis>
          {value}
        </Typography.Text>
      ),
    },
    { title: "쿠폰코드", dataIndex: "couponCode", align: "center", width: 120, ellipsis: true },
    {
      title: "타입",
      dataIndex: "couponType",
      align: "center",
      width: 90,
      render: (value) => <Tag>{getCouponTypeLabel(value)}</Tag>,
    },
    {
      title: "할인값",
      dataIndex: "discountValue",
      align: "center",
      width: 100,
      render: (_, item) =>
        item.discountType === "PERCENTAGE"
          ? `${item.discountValue}%`
          : `${item.discountValue.toLocaleString()}원`,
    },
    {
      title: "적용범위",
      dataIndex: "couponScope",
      align: "center",
      width: 110,
      render: (value) => getScopeLabel(value),
    },
    {
      title: "발급/한도",
      dataIndex: "issueLimit",
      align: "center",
      width: 120,
      render: (_, item) => (
        <div>
          <Typography.Text style={{ fontSize: 12 }}>
            {item.currentIssueCount?.toLocaleString()} /{" "}
            {item.issueLimit == null ? "∞" : item.issueLimit.toLocaleString()}
          </Typography.Text>
          {!item.canIssue ? <Tag color="red">발급불가</Tag> : null}
        </div>
      ),
    },
    {
      title: "활성",
      dataIndex: "isActive",
      align: "center",
      width: 80,
      render: (_, item) => (
        <Switch
          checked={item.isActive}
          onChange={() => onToggleActive(item)}
          disabled={disabled || loading}
        />
      ),
    },
    {
      title: "관리",
      dataIndex: "actions",
      width: 200,
      align: "right",
      fixed: "right",
      render: (_, item) => (
        <ManageBtns style={{ justifyContent: "flex-end" }}>
          <CustomButton onClick={() => onEdit(item)} disabled={disabled || loading}>
            수정
          </CustomButton>
          <CustomButton onClick={() => onCopy(item)} disabled={disabled || loading}>
            복사
          </CustomButton>
          <CustomButton
            customVariant="danger"
            onClick={() => onDelete(item)}
            disabled={disabled || loading}
          >
            삭제
          </CustomButton>
        </ManageBtns>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={list}
      rowKey={(item) => String(item.couponId)}
      pagination={false}
      loading={loading}
      locale={{ emptyText: "데이터가 없습니다." }}
      scroll={{ x: 1000 }}
    />
  );
}
